import React, { useState, useCallback, useEffect, useRef } from "react"
import { TfiWrite } from "react-icons/tfi"
import { useAuth0 } from "@auth0/auth0-react"
import { useQueryClient } from "@tanstack/react-query"
import useLogging from "../../../hooks/useLogging"
import axios from "axios"

// `futureHuddleData` prop is retained for backwards-compatible call sites but
// is no longer used internally — the React Query cache is the source of truth.
function FutureNotesSection({ day }) {
	const { getAccessTokenSilently } = useAuth0()
	const queryClient = useQueryClient()
	const [notes, setNotes] = useState("")
	const [isSaving, setIsSaving] = useState(false)
	const [lastSaved, setLastSaved] = useState(null)
	const [saveError, setSaveError] = useState(null)
	const logging = useLogging()
	const saveTimeoutRef = useRef(null)
	const initialLoadRef = useRef(false)
	// Track the latest text + whether it's unsaved so we can flush on blur/unmount.
	const latestNotesRef = useRef("")
	const dirtyRef = useRef(false)
	const mountedRef = useRef(true)

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	// Load notes when the component mounts or day changes
	useEffect(() => {
		if (day?.notes !== undefined) {
			setNotes(day.notes || "")
			latestNotesRef.current = day.notes || ""
			dirtyRef.current = false
			initialLoadRef.current = true
		}
	}, [day?.notes])

	// Persist notes. `withStatus` is false for fire-and-forget flushes on unmount
	// (the component is gone, so we must not touch state).
	const autoSave = useCallback(
		async (notesText, { withStatus = true } = {}) => {
			if (!initialLoadRef.current) return

			// Optimistically clear the dirty flag so a follow-up flush (blur then
			// unmount) doesn't fire the same save twice; restored on failure.
			dirtyRef.current = false

			try {
				if (withStatus && mountedRef.current) {
					setIsSaving(true)
					setSaveError(null)
				}
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})

				await axios.put(
					`${process.env.REACT_APP_BACKEND_URL}/api/future-huddle-notes`,
					{ date: day.date, notes: notesText },
					{
						headers: {
							Authorization: `Bearer ${token}`,
							"Content-Type": "application/json"
						}
					}
				)

				dirtyRef.current = false
				if (mountedRef.current) {
					setLastSaved(new Date())
					setSaveError(null)
				}

				// Update the React Query cache for any in-flight future-huddle queries
				// (queryKey shape: ["future-huddle", numDays, testEnvironment])
				queryClient.setQueriesData({ queryKey: ["future-huddle"] }, oldData => {
					if (!Array.isArray(oldData)) return oldData
					return oldData.map(item =>
						item.date === day.date ? { ...item, notes: notesText } : item
					)
				})
			} catch (error) {
				dirtyRef.current = true
				logging.error("Error auto-saving notes:", error)
				if (mountedRef.current) {
					setSaveError(error.response?.data?.errorMessage || error.message)
				}
			} finally {
				if (withStatus && mountedRef.current) setIsSaving(false)
			}
		},
		[day?.date, getAccessTokenSilently, logging, queryClient]
	)

	// Save immediately, cancelling any pending debounce.
	const flush = useCallback(() => {
		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current)
			saveTimeoutRef.current = null
		}
		if (dirtyRef.current) autoSave(latestNotesRef.current)
	}, [autoSave])

	// Handle text change with debounced auto-save
	const handleChange = useCallback(
		e => {
			const newNotes = e.target.value
			setNotes(newNotes)
			latestNotesRef.current = newNotes
			dirtyRef.current = true

			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current)
			}
			// Auto-save 2 seconds after the user stops typing
			saveTimeoutRef.current = setTimeout(() => {
				autoSave(newNotes)
			}, 2000)
		},
		[autoSave]
	)

	// On unmount (e.g. navigating back), flush any pending edit so a quick
	// type-then-leave within the debounce window isn't lost. Uses a latest-ref
	// so the cleanup runs exactly once on unmount, not whenever autoSave's
	// identity changes.
	const unmountFlushRef = useRef(null)
	unmountFlushRef.current = () => {
		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current)
			saveTimeoutRef.current = null
		}
		if (dirtyRef.current) {
			autoSave(latestNotesRef.current, { withStatus: false })
		}
	}
	useEffect(() => {
		return () => unmountFlushRef.current?.()
	}, [])

	const formatLastSaved = () => {
		if (!lastSaved) return ""
		return lastSaved.toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit"
		})
	}

	return (
		<div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
			<h3 className="text-lg font-semibold flex items-center mb-4 text-gray-800">
				<span className="mr-2 text-blue-600">
					<TfiWrite />
				</span>
				Daily Notes
			</h3>

			<div className="relative">
				<textarea
					className="w-full min-h-[120px] rounded-lg p-4 bg-gray-50 focus:bg-white resize-y text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-200 transition-colors"
					value={notes}
					onChange={handleChange}
					onBlur={flush}
					placeholder="Add your notes for this day..."
				/>

				<div className="flex justify-between items-center mt-2 text-xs text-gray-500">
					<div>{notes?.length > 0 ? `${notes.length} characters` : ""}</div>
					<div className="flex items-center">
						{isSaving && <span className="text-blue-600 mr-2">Saving...</span>}
						{saveError && !isSaving && (
							<span className="text-red-600 text-xs">Error: {saveError}</span>
						)}
						{lastSaved && !isSaving && !saveError && (
							<span className="text-green-600">Last saved at {formatLastSaved()}</span>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default React.memo(FutureNotesSection)
